/**
 * Reusable Ringover call history block — filters calls by phone(s).
 * Used both in the dossier detail page and in the Chelly Backup client drawer.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Loader2,
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(sec) {
  const s = Math.round(sec || 0);
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m${r.toString().padStart(2, "0")}s` : `${r}s`;
}

function CallRow({ call, onTranscribe, expanded, onToggleExpand, transcribing }) {
  const dir = call.direction === "out" || call.direction === "OUT" || call.type === "outgoing" ? "out" : "in";
  const answered = call.is_answered !== false && call.answered_time !== null;
  const state = (call.last_state || "").toUpperCase();
  const missed = state === "MISSED" || state === "VOICEMAIL" || !answered;
  const Icon = missed ? PhoneMissed : dir === "out" ? PhoneOutgoing : PhoneIncoming;
  const colorClass = missed ? "text-red-600" : dir === "out" ? "text-blue-600" : "text-emerald-600";

  const cid = String(call.call_id || call.id || call.cdr_id || "");
  const otherNumber = dir === "out" ? call.to_number : call.from_number;
  const hasRecord = !!(call.record || call.recording_url);
  const hasTranscript = !!call._transcript;
  const isTranscribing = transcribing === cid;

  return (
    <div className="border-b border-norea-border last:border-0" data-testid={`ringover-call-${cid}`}>
      <div className="flex items-center gap-3 py-2.5 px-3 hover:bg-norea-surfaceHover text-sm">
        <Icon className={`w-4 h-4 flex-shrink-0 ${colorClass}`} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-norea-text truncate">+{otherNumber}</div>
          <div className="text-[11px] text-norea-textMuted">
            {fmtDate(call.start_time || call.start_date)} · {fmtDuration(call.total_duration || call.duration)}
            {call.user?.firstname && <> · {call.user.firstname} {call.user.lastname}</>}
          </div>
        </div>
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
            missed ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {missed ? "Manqué" : "Répondu"}
        </span>
        {hasRecord && (hasTranscript ? (
          <button
            onClick={() => onToggleExpand(cid)}
            className="text-[11px] px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1"
            data-testid={`ringover-view-transcript-${cid}`}
          >
            <Sparkles className="w-3 h-3 text-norea-primary" />
            Transcript
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        ) : (
          <button
            onClick={() => onTranscribe(cid)}
            disabled={isTranscribing}
            className="text-[11px] px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1 disabled:opacity-50"
            data-testid={`ringover-transcribe-${cid}`}
          >
            {isTranscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Transcrire
          </button>
        ))}
      </div>
      {expanded && hasTranscript && (
        <div className="px-3 pb-3 bg-norea-bg space-y-2">
          {call._summary && (
            <div className="p-2 rounded bg-norea-primary/5 border border-norea-primary/20 text-xs whitespace-pre-wrap">
              <div className="font-semibold text-norea-primary mb-1 uppercase tracking-wider text-[10px]">Résumé IA</div>
              {call._summary}
            </div>
          )}
          <div className="p-2 rounded bg-white border border-norea-border text-xs whitespace-pre-wrap max-h-[240px] overflow-y-auto">
            <div className="font-semibold text-norea-textMuted mb-1 uppercase tracking-wider text-[10px]">Transcription</div>
            {call._transcript}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string[]} props.phones     - list of raw phone numbers (any format)
 * @param {string} [props.title]      - optional header text
 */
export default function RingoverCallHistory({ phones = [], title = "Historique d'appels Ringover" }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [transcribing, setTranscribing] = useState(null);

  const load = useCallback(async () => {
    const valid = (phones || []).filter(Boolean);
    if (valid.length === 0) {
      setCalls([]);
      return;
    }
    setLoading(true);
    try {
      // Ringover call log is fetched per phone, then deduped/merged client-side.
      const results = await Promise.all(
        valid.map((p) =>
          api
            .get(`/ringover/calls`, { params: { phone: p, limit_count: 50 } })
            .then((r) => r.data)
            .catch(() => ({ ok: false }))
        )
      );
      const seen = new Set();
      const merged = [];
      for (const r of results) {
        if (!r?.ok) continue;
        for (const c of r.calls || []) {
          const cid = String(c.call_id || c.id || "");
          if (cid && seen.has(cid)) continue;
          if (cid) seen.add(cid);
          merged.push(c);
        }
      }
      merged.sort((a, b) => {
        const ka = a.start_time || a.start_date || "";
        const kb = b.start_time || b.start_date || "";
        return kb.localeCompare(ka);
      });
      setCalls(merged);
    } finally {
      setLoading(false);
    }
  }, [phones]);

  useEffect(() => {
    load();
  }, [load]);

  const transcribe = async (callId) => {
    setTranscribing(callId);
    try {
      const r = await api.post(`/ringover/calls/${encodeURIComponent(callId)}/transcribe`);
      if (r.data?.ok) {
        setCalls((cs) =>
          cs.map((c) => {
            const cid = String(c.call_id || c.id || c.cdr_id || "");
            if (cid === callId) return { ...c, _transcript: r.data.transcript, _summary: r.data.summary };
            return c;
          })
        );
        setExpanded((e) => ({ ...e, [callId]: true }));
        toast.success(r.data.cached ? "Transcription déjà générée" : "Transcription générée");
      } else {
        toast.error(r.data?.error || "Transcription impossible");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription impossible");
    } finally {
      setTranscribing(null);
    }
  };

  return (
    <section className="bg-white border border-norea-border rounded-lg overflow-hidden" data-testid="ringover-history-panel">
      <div className="px-4 py-2.5 border-b border-norea-border bg-norea-bg flex items-center gap-3">
        <Phone className="w-4 h-4 text-norea-primary" />
        <div className="flex-1">
          <div className="font-semibold text-sm text-norea-text">{title}</div>
          <div className="text-[11px] text-norea-textMuted">
            {phones?.length > 0 ? `${phones.length} numéro(s)` : "Aucun numéro"} · {calls.length} appel(s)
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs px-2 py-1 rounded border border-norea-border hover:bg-norea-surfaceHover inline-flex items-center gap-1"
          data-testid="ringover-history-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Actualiser
        </button>
      </div>
      <div>
        {loading && calls.length === 0 ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-norea-primary mx-auto" /></div>
        ) : calls.length === 0 ? (
          <div className="p-6 text-center text-xs text-norea-textMuted italic">
            {phones.length === 0 ? "Aucun numéro sur ce contact." : "Aucun appel trouvé pour ces numéros."}
          </div>
        ) : (
          calls.map((c) => (
            <CallRow
              key={String(c.call_id || c.id || c.cdr_id)}
              call={c}
              onTranscribe={transcribe}
              transcribing={transcribing}
              expanded={!!expanded[String(c.call_id || c.id || c.cdr_id)]}
              onToggleExpand={(cid) => setExpanded((e) => ({ ...e, [cid]: !e[cid] }))}
            />
          ))
        )}
      </div>
    </section>
  );
}
